<template>
  <div class="loans-page">
    <header class="loans-page__header">
      <div>
        <h1>{{ $t('loans.title') }}</h1>
        <p>{{ $t('loans.subtitle') }}</p>
      </div>
      <div class="loans-page__header-actions">
        <v-text-field
          v-model="search"
          outlined
          dense
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          :placeholder="$t('loans.searchPlaceholder')"
          class="loans-page__search"
        />
        <button type="button" class="loans-page__add" @click="openCreate">
          <i class="mdi mdi-plus"></i>
          {{ $t('loans.add') }}
        </button>
      </div>
    </header>

    <section class="loans-page__kpi">
      <article class="kpi">
        <span>{{ $t('loans.openPlural') }}</span>
        <strong>{{ summary.open_count || 0 }}</strong>
        <em>{{ formatMoney(summary.open_amount) }}</em>
      </article>
      <article class="kpi kpi--warn">
        <span>{{ $t('loans.overdue') }}</span>
        <strong>{{ summary.overdue_count || 0 }}</strong>
        <em>{{ formatMoney(summary.overdue_amount) }}</em>
      </article>
      <article class="kpi kpi--ok">
        <span>{{ $t('loans.paid') }}</span>
        <strong>{{ summary.paid_count || 0 }}</strong>
        <em>{{ formatMoney(summary.paid_amount) }}</em>
      </article>
    </section>

    <section class="loans-page__dates">
      <label class="loans-page__date-field">
        <span>{{ $t('loans.dateFrom') }}</span>
        <button type="button" class="loans-page__date-btn" @click="filterDateTarget = 'from'">
          <i class="mdi mdi-calendar-month-outline"></i>
          <span>{{ dateFrom ? formatLoanDate(dateFrom) : $t('loans.selectDate') }}</span>
        </button>
      </label>
      <label class="loans-page__date-field">
        <span>{{ $t('loans.dateTo') }}</span>
        <button type="button" class="loans-page__date-btn" @click="filterDateTarget = 'to'">
          <i class="mdi mdi-calendar-month-outline"></i>
          <span>{{ dateTo ? formatLoanDate(dateTo) : $t('loans.selectDate') }}</span>
        </button>
      </label>
      <button type="button" class="loans-page__today" @click="setToday">
        {{ $t('common.today') }}
      </button>
      <button
        v-if="dateFrom || dateTo"
        type="button"
        class="loans-page__clear-dates"
        @click="clearDates"
      >
        {{ $t('loans.clearDates') }}
      </button>
    </section>

    <div class="loans-page__filters">
      <button
        v-for="tab in statusTabs"
        :key="tab.value"
        type="button"
        class="loans-page__filter"
        :class="{ 'loans-page__filter--active': status === tab.value }"
        @click="setStatus(tab.value)"
      >
        {{ $t(`loans.${tab.label}`) }}
      </button>
    </div>

    <div v-if="loans.length" class="loans-page__list">
      <article v-for="loan in loans" :key="loan.id" class="loan-card">
        <div class="loan-card__top">
          <div>
            <h3>{{ loan.name || $t('loans.unnamed') }}</h3>
            <div class="loan-card__phones">
              <a
                v-if="whatsappUrl(loan.phone)"
                class="loan-card__phone loan-card__phone--wa"
                :href="whatsappUrl(loan.phone)"
                target="_blank"
                rel="noopener noreferrer"
                :title="$t('loans.openWhatsapp')"
              >
                <i class="mdi mdi-whatsapp"></i>
                <span>{{ formatPhone(loan.phone) }}</span>
              </a>
              <span v-else class="loan-card__phone">{{ formatPhone(loan.phone) }}</span>
              <a
                v-for="(extra, idx) in loanExtraPhones(loan)"
                :key="`${loan.id}-extra-${idx}`"
                class="loan-card__phone"
                :href="whatsappUrl(extra) || `tel:${extra}`"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ formatPhone(extra) }}
              </a>
            </div>
          </div>
          <span class="loan-card__badge" :class="`loan-card__badge--${loan.display_status}`">
            {{ statusLabel(loan.display_status) }}
          </span>
        </div>

        <div class="loan-card__meta">
          <div>
            <span>{{ $t('loans.amount') }}</span>
            <strong>{{ formatMoney(loan.amount) }}</strong>
          </div>
          <div>
            <span>{{ $t('loans.paidAmount') }}</span>
            <strong>{{ formatMoney(loan.paid_amount || 0) }}</strong>
          </div>
          <div>
            <span>{{ $t('loans.remainingAmount') }}</span>
            <strong>{{ formatMoney(remainingAmount(loan)) }}</strong>
          </div>
          <div>
            <span>{{ $t('loans.dueDate') }}</span>
            <strong>{{ loan.date_back ? formatDate(loan.date_back) : '—' }}</strong>
          </div>
          <div>
            <span>{{ $t('loans.receipt') }}</span>
            <strong>{{ loan.order_id ? `№${loan.order_id}` : '—' }}</strong>
          </div>
          <div>
            <span>{{ $t('loans.created') }}</span>
            <strong>{{ formatDate(loan.created_at) }}</strong>
          </div>
        </div>

        <div class="loan-card__actions">
          <button
            v-if="loan.status === 'open' || loan.status === 'partial'"
            type="button"
            class="loan-card__btn"
            :disabled="busyId === loan.id"
            @click="askPay(loan)"
          >
            {{ $t('loans.pay') }}
          </button>
          <button
            v-if="loan.status === 'open' || loan.status === 'partial'"
            type="button"
            class="loan-card__btn loan-card__btn--ghost"
            :disabled="busyId === loan.id"
            @click="askCancel(loan)"
          >
            {{ $t('loans.cancelLoan') }}
          </button>
          <button
            type="button"
            class="loan-card__btn loan-card__btn--ghost"
            :disabled="busyId === loan.id"
            @click="openHistory(loan)"
          >
            {{ $t('loans.history') }}
          </button>
        </div>
      </article>
    </div>

    <div v-else class="loans-page__empty">
      <i class="mdi mdi-hand-coin-outline"></i>
      <p>{{ search || status ? $t('loans.nothingFound') : $t('loans.noLoans') }}</p>
      <span>{{ $t('loans.emptyText') }}</span>
      <button
        v-if="!search && !status"
        type="button"
        class="loans-page__add loans-page__add--empty"
        @click="openCreate"
      >
        <i class="mdi mdi-plus"></i>
        {{ $t('loans.add') }}
      </button>
    </div>

    <div v-if="pageCount > 1" class="loans-page__pagination">
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="5"
        color="#0b4f8a"
      />
    </div>

    <v-dialog v-model="createOpen" width="460" persistent content-class="loans-create-dialog">
      <div class="loan-modal">
        <header class="loan-modal__header">
          <div class="loan-modal__badge">
            <i class="mdi mdi-hand-coin-outline"></i>
          </div>
          <div>
            <h2>{{ $t('loans.addTitle') }}</h2>
            <p>{{ $t('loans.addText') }}</p>
          </div>
          <button type="button" class="loan-modal__close" :disabled="createSaving" @click="createOpen = false">
            <i class="mdi mdi-close"></i>
          </button>
        </header>

        <div class="loan-modal__body">
          <label class="loan-modal__field">
            <span>{{ $t('loans.debtorName') }}</span>
            <input
              v-model.trim="createForm.name"
              type="text"
              :placeholder="$t('loans.debtorNamePlaceholder')"
              maxlength="255"
            >
          </label>

          <div class="loan-modal__row">
            <label class="loan-modal__field">
              <span>{{ $t('loans.amount') }}</span>
              <div class="loan-modal__input-wrap">
                <input
                  v-model="createForm.amount"
                  type="number"
                  min="1"
                  step="1"
                  inputmode="numeric"
                  placeholder="0"
                >
                <em>₸</em>
              </div>
            </label>

            <label class="loan-modal__field">
              <span>{{ $t('loans.returnDate') }}</span>
              <button type="button" class="loan-modal__date" @click="createDateOpen = true">
                <i class="mdi mdi-calendar-month-outline"></i>
                <span>
                  {{ createForm.date_back
                    ? formatLoanDate(createForm.date_back)
                    : $t('loans.selectDate') }}
                </span>
              </button>
            </label>
          </div>

          <label class="loan-modal__field">
            <span>{{ $t('loans.whatsappPhone') }}</span>
            <input
              class="loan-modal__plain-input"
              :value="formatLoanPhoneInput(createForm.phone)"
              type="tel"
              inputmode="numeric"
              :placeholder="$t('loans.debtorPhonePlaceholder')"
              maxlength="18"
              @input="onCreatePhoneTyping"
            >
          </label>

          <div class="loan-modal__phones">
            <div
              v-for="(extra, idx) in createForm.extra_phones"
              :key="`extra-${idx}`"
              class="loan-modal__phone-row"
            >
              <label class="loan-modal__field">
                <span>{{ $t('loans.extraPhone') }} {{ idx + 1 }}</span>
                <input
                  class="loan-modal__plain-input"
                  :value="formatLoanPhoneInput(extra)"
                  type="tel"
                  inputmode="numeric"
                  :placeholder="$t('loans.debtorPhonePlaceholder')"
                  maxlength="18"
                  @input="onCreateExtraPhoneTyping(idx, $event)"
                >
              </label>
              <button
                type="button"
                class="loan-modal__phone-remove"
                :title="$t('loans.removePhone')"
                @click="removeCreateExtraPhone(idx)"
              >
                <i class="mdi mdi-close"></i>
              </button>
            </div>
            <button
              v-if="createForm.extra_phones.length < 5"
              type="button"
              class="loan-modal__phone-add"
              @click="addCreateExtraPhone"
            >
              <i class="mdi mdi-plus"></i>
              {{ $t('loans.addPhone') }}
            </button>
          </div>

          <label class="loan-modal__field">
            <span>{{ $t('loans.note') }}</span>
            <input
              v-model.trim="createForm.note"
              type="text"
              :placeholder="$t('loans.notePlaceholder')"
              maxlength="500"
            >
          </label>

          <div v-if="createDraftComplete" class="loan-modal__preview">
            <div>
              <span>{{ $t('loans.totalDebt') }}</span>
              <strong>{{ formatMoney(createForm.amount) }}</strong>
            </div>
            <div>
              <span>{{ $t('loans.returnBy') }}</span>
              <strong>{{ formatLoanDate(createForm.date_back) }}</strong>
            </div>
          </div>
          </div>

        <footer class="loan-modal__footer">
          <button
            type="button"
            class="loan-modal__btn loan-modal__btn--ghost"
            :disabled="createSaving"
            @click="createOpen = false"
          >
            {{ $t('loans.cancel') }}
          </button>
          <button
            type="button"
            class="loan-modal__btn"
            :disabled="createSaving"
            @click="confirmCreate"
          >
            <i v-if="createSaving" class="mdi mdi-loading mdi-spin"></i>
            <span>{{ $t('loans.addSave') }}</span>
          </button>
        </footer>
      </div>
    </v-dialog>

    <v-dialog width="320" v-model="createDateOpen" content-class="loans-create-date">
      <div class="loan-date">
        <v-date-picker
          v-model="createForm.date_back"
          :locale="datePickerLocale"
          full-width
          color="#0b4f8a"
          no-title
        />
        <button type="button" class="loan-modal__btn" @click="createDateOpen = false">
          {{ $t('loans.selectDateDone') }}
        </button>
        </div>
    </v-dialog>

    <v-dialog width="320" v-model="filterDateOpen" content-class="loans-create-date">
      <div class="loan-date">
        <v-date-picker
          v-model="filterDateDraft"
          :locale="datePickerLocale"
          full-width
          color="#0b4f8a"
          no-title
        />
        <button type="button" class="loan-modal__btn" @click="confirmFilterDate">
          {{ $t('loans.selectDateDone') }}
        </button>
        </div>
    </v-dialog>

    <v-dialog v-model="payOpen" max-width="460">
      <v-card class="loans-page__dialog">
        <v-card-title>{{ $t('loans.payTitle') }}</v-card-title>
        <v-card-text>
          <p class="loans-page__pay-text">
            {{ $t('loans.payText', { name: payName, amount: formatMoney(payAmount) }) }}
          </p>
          <div class="loans-page__pay-remaining">
            <span>{{ $t('loans.remainingAmount') }}</span>
            <strong>{{ formatMoney(payAmount) }}</strong>
      </div>
          <v-text-field
            v-model.number="repaymentAmount"
            outlined
            dense
            type="number"
            min="1"
            :max="payAmount"
            :label="$t('loans.repaymentAmount')"
            :hint="payHint"
            persistent-hint
            class="mt-3"
          />
          <div class="loans-page__pay-presets">
            <button
              v-for="preset in payPresets"
              :key="preset.key"
              type="button"
              class="loans-page__pay-preset"
              :class="{ 'loans-page__pay-preset--active': Number(repaymentAmount) === preset.value }"
              @click="setRepaymentPreset(preset.value)"
            >
              {{ preset.label }}
            </button>
    </div>
          <p v-if="payAfterRemaining != null" class="loans-page__pay-after">
            {{ $t('loans.afterPayRemaining', { amount: formatMoney(payAfterRemaining) }) }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="payOpen = false">{{ $t('loans.cancel') }}</v-btn>
          <v-btn color="#0b4f8a" dark :loading="busyId === payId" @click="confirmPay">
            {{ isPartialPay ? $t('loans.payPartial') : $t('loans.payFull') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelOpen" max-width="440">
      <v-card class="loans-page__dialog">
        <v-card-title>{{ $t('loans.cancelTitle') }}</v-card-title>
        <v-card-text>
          <p>{{ $t('loans.cancelText', { name: cancelName }) }}</p>
          <v-textarea
            v-model.trim="cancelReason"
            outlined
            dense
            rows="3"
            :label="$t('loans.cancelReason')"
            :placeholder="$t('loans.cancelReasonPlaceholder')"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelOpen = false">{{ $t('loans.cancel') }}</v-btn>
          <v-btn color="error" :loading="busyId === cancelId" @click="confirmCancel">{{ $t('loans.cancelLoan') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="historyOpen" max-width="480">
      <v-card class="loans-page__dialog">
        <v-card-title>{{ $t('loans.historyTitle') }}</v-card-title>
        <v-card-text>
          <div v-if="historyLoading" class="loans-page__history-loading">
            <i class="mdi mdi-loading mdi-spin"></i> {{ $t('common.loading') }}
          </div>
          <div v-else-if="history.length" class="loans-page__history">
            <div v-for="item in history" :key="item.id" class="loans-page__history-item">
              <strong>{{ auditLabel(item.action) }}</strong>
              <span>{{ item.actor ? (item.actor.name || item.actor.email) : '—' }} · {{ formatDate(item.created_at) }}</span>
              <p v-if="item.meta && item.meta.amount">
                {{ $t('loans.historyAmount', { amount: formatMoney(item.meta.amount) }) }}
                <template v-if="item.meta.remaining_amount != null">
                  · {{ $t('loans.remainingAmount') }}: {{ formatMoney(item.meta.remaining_amount) }}
                </template>
              </p>
              <p v-if="item.meta && item.meta.reason">{{ item.meta.reason }}</p>
            </div>
          </div>
          <p v-else class="loans-page__history-empty">{{ $t('loans.historyEmpty') }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="historyOpen = false">{{ $t('common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { cancelLoan, createLoan, getLoanHistory, getLoans, getLoansSummary, markLoanPaid } from '@/api/loans'
import { formatDate, formatMoney } from '@/utils/format'
import { notify, notifyError } from '@/utils/notify'

function todayIso() {
  const today = new Date()
  const offset = today.getTimezoneOffset() * 60000
  return new Date(today.getTime() - offset).toISOString().slice(0, 10)
}

export default {
  name: 'LoanInterface',
  data() {
    return {
      loans: [],
      page: 1,
      pageCount: 0,
      search: '',
      status: '',
      dateFrom: '',
      dateTo: '',
      summary: {},
      busyId: null,
      createOpen: false,
      createDateOpen: false,
      filterDateTarget: null,
      filterDateDraft: '',
      createSaving: false,
      createForm: {
        name: '',
        phone: '',
        extra_phones: [],
        amount: '',
        date_back: '',
        note: '',
      },
      payOpen: false,
      payId: null,
      payName: '',
      payAmount: 0,
      repaymentAmount: 0,
      cancelOpen: false,
      cancelId: null,
      cancelName: '',
      cancelReason: '',
      historyOpen: false,
      historyLoading: false,
      history: [],
      statusTabs: [
        { value: '', label: 'all' },
        { value: 'open', label: 'openPlural' },
        { value: 'partial', label: 'partialPlural' },
        { value: 'overdue', label: 'overduePlural' },
        { value: 'paid', label: 'paidPlural' },
        { value: 'cancelled', label: 'cancelledPlural' },
      ],
      formatMoney,
      formatDate,
    }
  },
  computed: {
    datePickerLocale() {
      return this.$i18n.locale === 'kk' ? 'kk-KZ' : 'ru-RU'
    },
    createDraftComplete() {
      const phone = String(this.createForm.phone || '').replace(/\D/g, '')
      return Boolean(
        this.createForm.name
        && Number(this.createForm.amount) > 0
        && this.createForm.date_back
        && phone.length === 11
      )
    },
    isPartialPay() {
      const amount = Number(this.repaymentAmount)
      return amount > 0 && amount < Number(this.payAmount)
    },
    payAfterRemaining() {
      const amount = Number(this.repaymentAmount)
      if (!amount || amount <= 0 || amount > Number(this.payAmount)) return null
      return Math.max(0, Number(this.payAmount) - amount)
    },
    payHint() {
      if (this.isPartialPay) return this.$t('loans.payPartialHint')
      if (Number(this.repaymentAmount) === Number(this.payAmount) && this.payAmount > 0) {
        return this.$t('loans.payFullHint')
      }
      return this.$t('loans.payAmountHint')
    },
    payPresets() {
      const remaining = Number(this.payAmount) || 0
      if (remaining <= 0) return []
      return [{ key: 'full', value: remaining, label: this.$t('loans.payFullPreset') }]
    },
    filterDateOpen: {
      get() {
        return !!this.filterDateTarget
      },
      set(open) {
        if (!open) this.filterDateTarget = null
      },
    },
  },
  mounted() {
    this.loadSummary()
    this.load()
  },
  watch: {
    page() {
      this.load()
    },
    search() {
      this.debouncedSearch()
    },
    dateFrom() {
      this.onDatesChange()
    },
    dateTo() {
      this.onDatesChange()
    },
    filterDateTarget(target) {
      if (!target) return
      this.filterDateDraft = target === 'from'
        ? (this.dateFrom || todayIso())
        : (this.dateTo || todayIso())
    },
  },
  methods: {
    dateParams() {
      const params = {}
      if (this.dateFrom) params.date_from = this.dateFrom
      if (this.dateTo) params.date_to = this.dateTo
      return params
    },
    onDatesChange() {
      this.resetToFirstPage()
      this.loadSummary()
    },
    setToday() {
      const today = todayIso()
      this.dateFrom = today
      this.dateTo = today
    },
    clearDates() {
      this.dateFrom = ''
      this.dateTo = ''
    },
    confirmFilterDate() {
      const value = this.filterDateDraft || ''
      if (this.filterDateTarget === 'from') {
        this.dateFrom = value
      } else if (this.filterDateTarget === 'to') {
        this.dateTo = value
      }
      this.filterDateTarget = null
    },
    formatPhone(phone) {
      if (!phone) return '—'
      const digits = String(phone).replace(/\D/g, '')
      if (digits.length === 11) {
        return `${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`
      }
      return phone
    },
    whatsappUrl(phone) {
      let digits = String(phone || '').replace(/\D/g, '')
      if (!digits) return null
      if (digits.length === 11 && digits[0] === '8') {
        digits = `7${digits.slice(1)}`
      }
      if (digits.length < 10) return null
      return `https://wa.me/${digits}`
    },
    loanExtraPhones(loan) {
      const list = loan && loan.extra_phones
      return Array.isArray(list) ? list.filter(Boolean) : []
    },
    statusLabel(status) {
      if (status === 'paid') return this.$t('loans.paid')
      if (status === 'overdue') return this.$t('loans.overdue')
      if (status === 'partial') return this.$t('loans.partial')
      if (status === 'cancelled') return this.$t('loans.cancelled')
      return this.$t('loans.open')
    },
    remainingAmount(loan) {
      return Math.max(0, Number(loan.amount || 0) - Number(loan.paid_amount || 0))
    },
    setStatus(status) {
      this.status = status
      this.resetToFirstPage()
    },
    resetToFirstPage() {
      if (this.page !== 1) {
        this.page = 1
      } else {
        this.load()
      }
    },
    debouncedSearch: debounce(function () {
      this.resetToFirstPage()
    }, 350),
    async loadSummary() {
      try {
        this.summary = await getLoansSummary(this.dateParams())
      } catch (error) {
        this.summary = {}
      }
    },
    async load() {
      try {
        const params = { page: this.page, ...this.dateParams() }
        if (this.search) params.search = this.search
        if (this.status) params.status = this.status

        const data = await getLoans(params)
        this.loans = data.data || []
        this.pageCount = Number(data.last_page) || 0
      } catch (error) {
        notifyError(error)
      }
    },
    defaultDueDate() {
      const date = new Date()
      date.setDate(date.getDate() + 7)
      const offset = date.getTimezoneOffset() * 60000
      return new Date(date.getTime() - offset).toISOString().slice(0, 10)
    },
    openCreate() {
      this.createForm = {
        name: '',
        phone: '',
        extra_phones: [],
        amount: '',
        date_back: this.defaultDueDate(),
        note: '',
      }
      this.createDateOpen = false
      this.createOpen = true
    },
    onCreatePhoneTyping(event) {
      this.createForm.phone = String((event && event.target && event.target.value) || '')
        .replace(/\D/g, '')
        .slice(0, 11)
    },
    addCreateExtraPhone() {
      if (this.createForm.extra_phones.length >= 5) return
      this.createForm.extra_phones.push('')
    },
    removeCreateExtraPhone(index) {
      this.createForm.extra_phones.splice(index, 1)
    },
    onCreateExtraPhoneTyping(index, event) {
      const digits = String((event && event.target && event.target.value) || '')
        .replace(/\D/g, '')
        .slice(0, 11)
      this.$set(this.createForm.extra_phones, index, digits)
    },
    normalizedCreateExtraPhones() {
      const primary = String(this.createForm.phone || '').replace(/\D/g, '')
      const seen = { [primary]: true }
      return (this.createForm.extra_phones || [])
        .map((item) => String(item || '').replace(/\D/g, ''))
        .filter((digits) => {
          if (!digits || digits.length !== 11 || seen[digits]) return false
          seen[digits] = true
          return true
        })
    },
    formatLoanPhoneInput(value) {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 11)
      if (!digits) return ''
      let out = digits[0] || ''
      if (digits.length > 1) out += ` (${digits.slice(1, 4)}`
      if (digits.length >= 4) out += ')'
      if (digits.length > 4) out += ` ${digits.slice(4, 7)}`
      if (digits.length > 7) out += `-${digits.slice(7, 9)}`
      if (digits.length > 9) out += `-${digits.slice(9, 11)}`
      return out
    },
    formatLoanDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString(this.datePickerLocale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    },
    async confirmCreate() {
      const name = String(this.createForm.name || '').trim()
      const phone = String(this.createForm.phone || '').replace(/\D/g, '')
      const amount = Number(this.createForm.amount)
      const dateBack = this.createForm.date_back

      if (!name) {
        notify(this.$t('loans.nameRequired'), 'warning')
        return
      }
      if (phone.length !== 11) {
        notify(this.$t('loans.phoneRequired'), 'warning')
        return
      }
      const incompleteExtra = (this.createForm.extra_phones || []).some((item) => {
        const digits = String(item || '').replace(/\D/g, '')
        return digits.length > 0 && digits.length !== 11
      })
      if (incompleteExtra) {
        notify(this.$t('loans.extraPhoneInvalid'), 'warning')
        return
      }
      if (!amount || amount <= 0) {
        notify(this.$t('loans.amountRequired'), 'warning')
        return
      }
      if (!dateBack) {
        notify(this.$t('loans.dueDateRequired'), 'warning')
        return
      }

      this.createSaving = true
      try {
        const result = await createLoan({
          name,
          phone,
          extra_phones: this.normalizedCreateExtraPhones(),
          amount,
          date_back: dateBack,
          note: this.createForm.note || null,
        })
        notify(result.message || this.$t('loans.createdMessage'))
        this.createOpen = false
        await Promise.all([this.load(), this.loadSummary()])
      } catch (error) {
        notifyError(error)
      } finally {
        this.createSaving = false
      }
    },
    askPay(loan) {
      this.payId = loan.id
      this.payName = loan.name || `№${loan.id}`
      this.payAmount = this.remainingAmount(loan)
      this.repaymentAmount = this.payAmount
      this.payOpen = true
    },
    setRepaymentPreset(value) {
      this.repaymentAmount = Number(value) || 0
    },
    async confirmPay() {
      const amount = Number(this.repaymentAmount)
      if (!amount || amount <= 0) {
        notify(this.$t('loans.repaymentAmountRequired'), 'warning')
        return
      }
      if (amount > this.payAmount) {
        notify(this.$t('loans.repaymentAmountTooHigh'), 'warning')
        return
      }

      this.busyId = this.payId
      try {
        const result = await markLoanPaid(this.payId, amount)
        notify(result.message || (amount === this.payAmount ? this.$t('loans.repaid') : this.$t('loans.partialRepaid')))
        this.payOpen = false
        await Promise.all([this.load(), this.loadSummary()])
      } catch (error) {
        notifyError(error)
      } finally {
        this.busyId = null
      }
    },
    askCancel(loan) {
      this.cancelId = loan.id
      this.cancelName = loan.name || `№${loan.id}`
      this.cancelReason = ''
      this.cancelOpen = true
    },
    async confirmCancel() {
      if (this.cancelReason.length < 3) {
        notify(this.$t('loans.cancelReasonRequired'), 'warning')
        return
      }

      this.busyId = this.cancelId
      try {
        await cancelLoan(this.cancelId, this.cancelReason)
        notify(this.$t('loans.cancelledMessage'), 'warning')
        this.cancelOpen = false
        await Promise.all([this.load(), this.loadSummary()])
      } catch (error) {
        notifyError(error)
      } finally {
        this.busyId = null
      }
    },
    auditLabel(action) {
      const labels = {
        created: 'actionCreated',
        paid: 'actionPaid',
        repayment: 'actionRepayment',
        cancelled: 'actionCancelled',
      }
      return this.$t(`loans.${labels[action] || 'history'}`)
    },
    async openHistory(loan) {
      this.historyOpen = true
      this.historyLoading = true
      this.history = []
      try {
        this.history = await getLoanHistory(loan.id)
      } catch (error) {
        notifyError(error)
      } finally {
        this.historyLoading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.loans-page {
  padding: 28px 32px 40px;
  color: #152033;
}

.loans-page__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 22px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 750;
    letter-spacing: -0.02em;
  }

  p {
    margin: 6px 0 0;
    color: #6b7a90;
    font-size: 14px;
  }
}

.loans-page__header-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.loans-page__search {
  width: 300px;
  max-width: 100%;
  background: #fff;
  border-radius: 12px;
}

.loans-page__add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 14px;
  border: 0;
  border-radius: 10px;
  background: #0b4f8a;
  color: #fff;
  font-weight: 700;
    cursor: pointer;
  white-space: nowrap;

  i {
    font-size: 18px;
  }

  &--empty {
    margin-top: 16px;
  }
}

.loans-page__kpi {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.kpi {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #d9e2ec;
  border-radius: 16px;

  span {
    font-size: 13px;
    color: #6b7a90;
  }

  strong {
    font-size: 24px;
    font-weight: 750;
  }

  em {
    font-style: normal;
    font-size: 13px;
    color: #3d4f63;
  }

  &--warn {
    border-color: #f3d5b5;
    background: #fff8f1;
  }

  &--ok {
    border-color: #c7e6dc;
    background: #f3fbf8;
  }
}

.loans-page__dates {
    display: flex;
    flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid #d9e2ec;
  border-radius: 14px;
  background: #fff;
}

.loans-page__date-field {
  display: grid;
  gap: 4px;
  min-width: min(220px, 100%);

  > span {
    color: #6b7a90;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
}

.loans-page__date-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid #d9e2ec;
  border-radius: 12px;
  background: #fff;
  color: #152033;
  cursor: pointer;
  text-align: left;

  .mdi {
    color: #0b4f8a;
    font-size: 20px;
  }

  span {
    font-size: 14px;
    font-weight: 650;
  }
}

.loans-page__pay-text {
  margin: 0 0 12px;
  color: #3d4f63;
  line-height: 1.45;
}

.loans-page__pay-remaining {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f4f7fb;
  color: #152033;

  span {
    color: #6b7a90;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  strong {
    font-size: 18px;
  }
}

.loans-page__pay-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.loans-page__pay-preset {
  min-height: 36px;
  padding: 0 12px;
  border: 1px solid #d9e2ec;
  border-radius: 999px;
  background: #fff;
  color: #3d4f63;
  font-weight: 700;
  cursor: pointer;

  &--active {
    border-color: #0b4f8a;
    background: #0b4f8a;
    color: #fff;
  }
}

.loans-page__pay-after {
  margin: 12px 0 0;
  color: #0b4f8a;
  font-size: 13px;
  font-weight: 650;
}

.loans-page__today,
.loans-page__clear-dates {
  min-height: 38px;
  padding: 0 12px;
  border-radius: 9px;
  font-weight: 700;
  cursor: pointer;
}

.loans-page__today {
  border: 0;
  background: #0b4f8a;
  color: #fff;
}

.loans-page__clear-dates {
  border: 1px solid #d9e2ec;
  background: #fff;
  color: #3d4f63;
}

.loans-page__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.loans-page__filter {
  padding: 8px 14px;
  border: 1px solid #d9e2ec;
  border-radius: 999px;
  background: #fff;
  cursor: pointer;
  font-weight: 650;
  color: #3d4f63;

  &--active {
    background: #0b4f8a;
    border-color: #0b4f8a;
    color: #fff;
  }
}

.loans-page__list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
}

.loan-card {
  padding: 16px;
  background: #fff;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
}

.loan-card__top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
  }

  p {
    margin: 4px 0 0;
    color: #6b7a90;
    font-size: 13px;
  }
}

.loan-card__phones {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin-top: 4px;
}

.loan-card__phone {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #6b7a90;
  font-size: 13px;
  text-decoration: none;

  &--wa {
    color: #128c7e;
    font-weight: 650;

    &:hover {
      color: #0b6b60;
      text-decoration: underline;
    }
  }

  .mdi-whatsapp {
    font-size: 16px;
  }
}

.loan-card__badge {
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 650;

  &--open {
    background: #e8f1f8;
    color: #0b4f8a;
  }

  &--partial {
    background: #fff7e6;
    color: #b45309;
  }

  &--overdue {
    background: #fff1f0;
    color: #b42318;
  }

  &--paid {
    background: #e8f8f2;
    color: #0f766e;
  }

  &--cancelled {
    background: #f1f3f5;
    color: #5f6b7a;
  }
}

.loan-card__meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 14px;

  span {
    display: block;
    font-size: 12px;
    color: #6b7a90;
  }

  strong {
    font-size: 14px;
  }
}

.loan-card__actions {
  display: flex;
  gap: 8px;
}

.loan-card__btn {
  flex: 1;
  padding: 10px 12px;
  border: 0;
  border-radius: 10px;
  background: #0b4f8a;
  color: #fff;
  font-weight: 650;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }

  &--ghost {
    background: #fff;
    border: 1px solid #d9e2ec;
    color: #3d4f63;
  }
}

.loans-page__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 240px;
  padding: 48px;
  text-align: center;
  color: #6b7a90;
  border: 1px dashed #d9e2ec;
  border-radius: 16px;
  background: #fff;

  .mdi {
    font-size: 40px;
    color: #9eb6cc;
  }

  p {
    margin: 0;
    color: #152033;
    font-size: 16px;
    font-weight: 700;
  }
}

.loans-page__pagination {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}

.loans-page__history-loading,
.loans-page__history-empty {
  color: #6b7a90;
}

.loans-page__history {
  display: grid;
  gap: 10px;
}

.loans-page__history-item {
  display: grid;
  gap: 3px;
  padding: 11px 12px;
  border: 1px solid #d9e2ec;
  border-radius: 10px;

  strong {
    color: #152033;
  }

  span,
  p {
    margin: 0;
    color: #6b7a90;
    font-size: 13px;
  }
}

.loans-page__dialog {
  border-radius: 16px !important;
}

.loan-modal {
  overflow: hidden;
  border-radius: 22px;
  background:
    radial-gradient(circle at top right, rgba(251, 191, 36, 0.16), transparent 34%),
    linear-gradient(180deg, #fffdf9 0%, #ffffff 48%);
  border: 1px solid #edd6ba;
  box-shadow: 0 24px 60px rgba(61, 36, 16, 0.18);
  color: #1f2937;
}

.loan-modal__header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 22px 22px 8px;

  h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 750;
    letter-spacing: -0.02em;
    color: #1c1917;
  }

  p {
    margin: 4px 0 0;
    font-size: 13px;
    color: #8b7355;
  }
}

.loan-modal__badge {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, #fff7ed, #ffedd5);
  color: #c2410c;
  border: 1px solid #fdba74;

  .mdi {
    font-size: 24px;
  }
}

.loan-modal__close {
  margin-left: auto;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 10px;
  background: #f5efe7;
  color: #78716c;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #ebe4da;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.loan-modal__body {
  padding: 12px 22px 8px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.loan-modal__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.loan-modal__phones {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.loan-modal__phone-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;

  .loan-modal__field {
    flex: 1;
    min-width: 0;
  }
}

.loan-modal__phone-remove {
  width: 42px;
  height: 48px;
  flex-shrink: 0;
  border: 1px solid #e7d5c0;
  border-radius: 14px;
  background: #fff;
  color: #9a3412;
  cursor: pointer;
  display: grid;
  place-items: center;

  .mdi {
    font-size: 18px;
  }
}

.loan-modal__phone-add {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 12px;
  border: 1px dashed #d6c3ad;
  border-radius: 999px;
  background: transparent;
  color: #78716c;
  font-weight: 650;
  cursor: pointer;
}

.loan-modal__field {
  display: flex;
  flex-direction: column;
  gap: 7px;

  > span {
    font-size: 12px;
    font-weight: 650;
    color: #78716c;
    letter-spacing: 0.02em;
  }

  input {
  width: 100%;
    padding: 13px 14px;
    border: 1px solid #e7d5c0;
    border-radius: 14px;
    background: #fff;
    outline: none;
    font-size: 15px;
    color: #1c1917;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      border-color: #d97706;
      box-shadow: 0 0 0 4px rgba(217, 119, 6, 0.12);
    }
  }
}

.loan-modal__plain-input,
.loan-modal__input-wrap {
  width: 100%;
  min-height: 48px;
  padding: 12px 14px;
  border: 1px solid #e7d5c0;
  border-radius: 14px;
  background: #fff;
  color: #1c1917;
  font-size: 18px;
  font-weight: 750;
  outline: none;
}

.loan-modal__input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;

  input {
    flex: 1;
    min-width: 0;
    min-height: auto;
    padding: 0;
    border: 0;
    outline: none;
    background: transparent;
    font: inherit;
    font-weight: 750;
    box-shadow: none;

    &:focus {
      border: 0;
      box-shadow: none;
    }
  }

  em {
    color: #a8a29e;
    font-style: normal;
    font-weight: 700;
  }
}

.loan-modal__date {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 0 14px;
  border: 1px solid #e7d5c0;
  border-radius: 14px;
  background: #fff;
  color: #1c1917;
  cursor: pointer;
  text-align: left;

  .mdi {
    color: #d97706;
    font-size: 20px;
  }

  span {
    font-size: 14px;
    font-weight: 600;
  }
}

.loan-modal__preview {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 14px;
  border-radius: 16px;
  background: #1c1917;
  color: #fff;

  span {
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.62);
  }

  strong {
    font-size: 16px;
    font-weight: 750;
  }
}

.loan-modal__footer {
  display: flex;
  gap: 10px;
  padding: 16px 22px 22px;
}

.loan-modal__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 1;
  min-height: 46px;
  border: 0;
  border-radius: 14px;
  background: #0b4f8a;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: #093f6d;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  &--ghost {
    background: #f5efe7;
    color: #57534e;

    &:hover:not(:disabled) {
      background: #ebe4da;
    }
  }
}

.loan-date {
  padding: 12px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid #e7d5c0;
  box-shadow: 0 18px 40px rgba(61, 36, 16, 0.16);

  .loan-modal__btn {
    width: 100%;
  margin-top: 10px;
  }
}

@media (max-width: 720px) {
  .loans-page {
    padding: 20px 16px 32px;
  }

  .loans-page__header {
    flex-direction: column;
  }

  .loans-page__search {
    width: 100%;
  }

  .loans-page__kpi {
    grid-template-columns: 1fr;
  }

  .loan-modal__row,
  .loan-modal__preview {
    grid-template-columns: 1fr;
  }
}
</style>

<style lang="scss">
.loans-create-dialog {
  box-shadow: none !important;
  background: transparent !important;
  overflow: visible !important;
}

.loans-create-date {
  box-shadow: none !important;
  background: transparent !important;
}
</style>
